import { Badge, ContactCallSplitButton } from "@/components/ui";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner/LoadingSpinner";
import {
  Card,
  CardContent,
  CardFooter,
} from "@/components/ui/shadcn/Card/Card";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { retrieveContactQueryOptions } from "@/lib/data/react-query/contacts/contact";
import type {
  CONTACT_INDIVIDUAL_SUBTYPES,
  CONTACT_ORGANIZATION_SUBTYPES,
} from "@/lib/data/static/contactStatics";
import type {
  Contact,
  ContactFax,
  ContactTagConnection,
  Matter,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getMatterTagChipClassNames } from "@/lib/utils/matters/matter-tag-color";
import {
  formatAddress,
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import { MultiValueCell } from "@/lib/utils/tableUtils";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { startCase } from "lodash";
import { CircleAlert, Pencil, XIcon } from "lucide-react";
import { Suspense, useMemo } from "react";
import { Button } from "../shadcn/Button/Button";
import { Typography } from "../Typography/Typography";

export type ContactPreviewChipContact = Pick<
  Contact,
  | "id"
  | "individualFirstName"
  | "type"
  | "individualLastName"
  | "organizationName"
  | "emails"
  | "addresses"
  | "phones"
> & {
  contactName?: string;
  faxes?: ContactFax[];
  tagConnections?: ContactTagConnection[];
  individualSubTypes?:
    | readonly (typeof CONTACT_INDIVIDUAL_SUBTYPES)[number][]
    | null
    | undefined;
  organizationSubTypes?:
    | readonly (typeof CONTACT_ORGANIZATION_SUBTYPES)[number][]
    | null
    | undefined;
};

export interface ContactPreviewChipProps {
  matter?: Matter;
  disableDelete?: boolean;
  onDelete?: (contact: ContactPreviewChipContact) => void;
  onClick?: () => void;
  className?: string;
}

type ConditionalProps =
  | {
      contact?: never;
      contactId: string;
    }
  | {
      contact: ContactPreviewChipContact;
      contactId?: never;
    };

export const DangerousContactPreviewChip = ({
  contact: contactFromProps,
  contactId,
  disableDelete,
  onDelete,
  onClick,
  matter,
  className,
}: ContactPreviewChipProps & ConditionalProps) => {
  const hasViewTextPermission = true;
  const {
    data: contactData,
    isSuccess,
    isLoading,
  } = useQuery({
    ...retrieveContactQueryOptions(contactId),
    enabled: !!contactId,
  });
  const contact = contactId ? contactData?.data : contactFromProps;

  const { openCommunicationModal } = useGlobalModal();
  const contactSubTypes = useMemo(() => {
    if (!contact) {
      return undefined;
    }
    if (contact.type === "individual") {
      return contact.individualSubTypes;
    }
    if (contact.type === "organization") {
      return contact.organizationSubTypes;
    }
    return undefined;
  }, [contact]);

  if (!contact && isSuccess) {
    throw new Error("Contact not found");
  }

  if (!contact || isLoading) {
    return <ContactPreviewChipLoadingState className={className} />;
  }

  const contactTags = contact.tagConnections ?? [];
  const visibleTags = contactTags.slice(0, 3);
  const hiddenTags = contactTags.slice(3);
  const hasContactTags = contactTags.length > 0;
  const hiddenTagLabels = hiddenTags
    .map((tag) => tag.label)
    .filter((label): label is string => Boolean(label));

  return (
    <Card className={cn("relative flex w-full flex-col", className)}>
      {onDelete && !disableDelete && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => {
            onDelete(contact as ContactPreviewChipContact);
          }}
          className="absolute top-4 right-4 h-4 w-4"
          data-testid="contact-preview-chip-delete"
        >
          <XIcon className="h-4 w-4" />
        </Button>
      )}
      <CardContent className="w-full flex-1 p-0 px-6 py-6">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <Typography
                weight="semibold"
                data-testid="contact-preview-chip-name"
                className="max-w-[18rem] truncate"
              >
                {/* @ts-expect-error - contactName is being wrongly called an error */}
                {contact.contactName ?? formatContactsName(contact)}
              </Typography>
              {onClick && (
                <Button
                  type="button"
                  className="h-6 w-6"
                  variant="ghost"
                  size="icon"
                  onClick={onClick}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs">
                {hasContactTags && (
                  <>
                    {visibleTags.map((tag) => (
                      <Badge
                        key={tag.id}
                        className={cn(
                          "border px-2.5 py-0.5 text-xs font-semibold",
                          getMatterTagChipClassNames(tag.color ?? undefined),
                        )}
                      >
                        {tag.label}
                      </Badge>
                    ))}
                    {hiddenTags.length > 0 && (
                      <TooltipWrapper message={hiddenTagLabels.join(", ")}>
                        <div>
                          <Badge className="bg-muted text-muted-foreground hover:bg-muted-foreground w-fit cursor-default px-2.5 py-0.5 text-xs font-semibold transition-colors hover:text-white">
                            +{hiddenTags.length}
                          </Badge>
                        </div>
                      </TooltipWrapper>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="mb-4 flex items-baseline justify-start gap-2">
          <Typography size="xs" variant="subHeading">
            {startCase(contact.type ?? "")}
          </Typography>
          {contactSubTypes && contactSubTypes.length > 0 && (
            <>
              <Typography size="xs" variant="subHeading">
                •
              </Typography>
              <Typography size="xs" variant="subHeading">
                <MultiValueCell
                  value={contactSubTypes.map((subType) => startCase(subType))}
                  showItemCount={3}
                  badgeClassName="h-4 text-xs"
                />
              </Typography>
            </>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col">
            <Typography size="sm" variant="subHeading">
              Primary phone
            </Typography>
            <Typography size="sm">
              <MultiValueCell
                value={contact.phones.map((phone) =>
                  formatLocalOrIntlPhoneNumber(phone),
                )}
              />
            </Typography>
          </div>
          <div className="flex flex-col">
            <Typography size="sm" variant="subHeading">
              Primary email
            </Typography>
            <Typography size="sm">
              <MultiValueCell
                value={contact.emails.map((email) => email.address ?? "")}
              />
            </Typography>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="flex flex-col">
            <Typography size="sm" variant="subHeading">
              Primary fax
            </Typography>
            <Typography size="sm">
              <MultiValueCell
                value={contact.faxes?.map((fax) =>
                  formatLocalOrIntlPhoneNumber(fax),
                )}
              />
            </Typography>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="flex flex-col">
            <Typography size="sm" variant="subHeading">
              Primary address
            </Typography>
            <Typography size="sm">
              <MultiValueCell
                value={contact.addresses.map((address) =>
                  formatAddress({
                    street1: address.street1 ?? undefined,
                    street2: address.street2 ?? undefined,
                    city: address.city ?? undefined,
                    state: address.state ?? "",
                    zipCode: address.zipCode ?? "",
                    country:
                      address.country === "United States"
                        ? undefined
                        : (address.country ?? undefined),
                  }),
                )}
              />
            </Typography>
          </div>
        </div>
      </CardContent>
      <CardFooter className="mt-auto flex justify-center gap-2 border-t p-2">
        <TooltipWrapper
          message={
            contact.phones.length === 0 &&
            "Add a phone number to send a text message"
          }
        >
          <div>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                openCommunicationModal({
                  contactId: contact.id,
                  matterId: matter?.id,
                });
              }}
              disabled={contact.phones.length === 0 || !hasViewTextPermission}
            >
              Send text
              {contact.phones.length === 0 && (
                <CircleAlert className="ml-2 h-4 w-4" />
              )}
            </Button>
          </div>
        </TooltipWrapper>
        <ContactCallSplitButton
          contactId={contact.id}
          phones={contact.phones}
          size="sm"
        />
      </CardFooter>
    </Card>
  );
};

const ContactPreviewChipLoadingState = ({
  className,
}: {
  className?: string;
}) => {
  return (
    <Card className={cn("relative flex w-full flex-col", className)}>
      <CardContent className="h-[311px] w-full p-0 px-6 py-6">
        <div className="flex h-full items-center justify-center">
          <Typography weight="semibold" data-testid="contact-preview-chip-name">
            <LoadingSpinner />
          </Typography>
        </div>
      </CardContent>
    </Card>
  );
};

export const ContactPreviewChip = (
  props: ContactPreviewChipProps & ConditionalProps,
) => (
  <Suspense
    fallback={<ContactPreviewChipLoadingState className={props.className} />}
  >
    <ErrorBoundary fallback={<div>Error loading this contact.</div>}>
      <DangerousContactPreviewChip {...props} />
    </ErrorBoundary>
  </Suspense>
);
